import { ResumeFile } from '../db/ResumeFile.js';
import { map, of } from 'rxjs';
import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/buchungsheld-db/server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';

export const getResumeFilesByIDs = registerQuery({
  operationName: 'getResumeFilesByIDs',
  type: () => [ResumeFile],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<ResumeFile>>(gqlFields: SelSet) =>
      readEntitiesByIDs(ResumeFile, ids, gqlFields),
});

export const subscribeResumeFilesByIDs = registerSubscription({
  operationName: 'subscribeResumeFilesByIDs',
  type: () => [ResumeFile],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<ResumeFile>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(ResumeFile, ids, gqlFields),
});

export const getAllResumeFiles = registerQuery({
  operationName: 'getAllResumeFiles',
  type: () => [ResumeFile],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<ResumeFile>>(gqlFields: SelSet) =>
      readAllEntities(ResumeFile, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllResumeFiles = registerSubscription({
  operationName: 'subscribeAllResumeFiles',
  type: () => [ResumeFile],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<ResumeFile>>(gqlFields: SelSet) =>
      watchAllEntities(ResumeFile, gqlFields, { skip, limit, excludeIDs }),
});

export const getResumeFileByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<ResumeFile>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getResumeFilesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeResumeFileByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<ResumeFile>>(gqlFields: SelSet) =>
    id ? subscribeResumeFilesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
